import type {ReactElement, RefObject} from 'react';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {ClientRect, CollisionDetection, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {closestCenter, DndContext, DragOverlay, MouseSensor, rectIntersection, TouchSensor, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {LayerRow} from './components/layer-row/layer-row';
import {LayerGroupRow} from './components/layer-group-row/layer-group-row';
import type {LayerEntry, LayerGroupEntry} from './layers-panel.types';
import {applySelection, getDraggedLayer, getSortableId, handleDragEnd} from './layers-panel.helper';
import {selectLayerPanelRows} from '@Components/poster-editor/poster-editor-reducer';
import {GA4EventName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {DRILL_IN_EVENT_TYPE} from '@PosterWhiteboard/items/item/item.types';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';
import styles from './layers-panel.module.scss';
import layerRowStyles from './components/layer-row/layer-row.module.scss';

interface LayersPanelProps {
  onClose: () => void;
  hideHeader?: boolean;
  bodyRef?: RefObject<HTMLDivElement | null>;
}

const DRAG_DISTANCE_PX = 8;
const TOUCH_LONG_PRESS_DELAY_MS = 250;
const TOUCH_LONG_PRESS_TOLERANCE_PX = 5;
const DRAG_OVERLAY_Z_INDEX_ABOVE_POPUPS = 10100;
const EMPTY_ACTIVE_IDS: readonly string[] = [];
const DRAG_MODIFIERS = [restrictToVerticalAxis];

export function LayersPanel({onClose, hideHeader = false, bodyRef}: LayersPanelProps): ReactElement {
  const entries = useAppSelector(selectLayerPanelRows);
  const isSingleEntry = entries.length === 1;
  const activeIds = useAppSelector((state: RootState): readonly string[] => {
    const currentPageId = state.posterEditor.currentPageId;
    return state.posterEditor.activeItemIds[currentPageId] ?? EMPTY_ACTIVE_IDS;
  });
  const activeIdsSet = useMemo(() => new Set(activeIds), [activeIds]);
  const outerIds = useMemo(() => entries.map(getSortableId), [entries]);
  const memberToGroupId = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of entries) {
      if (entry.layerType !== 'group') {
        continue;
      }
      for (const uid of entry.memberUids) {
        map.set(uid, entry.groupId);
      }
    }
    return map;
  }, [entries]);

  const toggleSelection = (page: Page, targetUids: readonly string[]): void => {
    if (targetUids.length === 0) {
      return;
    }
    const targetUidSet = new Set(targetUids);
    const allSelected = targetUids.every((uid) => activeIdsSet.has(uid));
    const withoutTargets = activeIds.filter((uid) => !targetUidSet.has(uid));
    applySelection(page, allSelected ? withoutTargets : [...withoutTargets, ...targetUids]);
  };

  const replaceCurrentSelection = (page: Page, targetUids: readonly string[], selectionEvent?: MouseEvent): void => {
    if (targetUids.length === 0) {
      return;
    }
    if (activeIdsSet.size === targetUids.length && targetUids.every((uid) => activeIdsSet.has(uid))) {
      page.activeSelection.discardActiveObject();
      return;
    }
    applySelection(page, targetUids, selectionEvent);
  };

  const handleItemClick = (uid: string): void => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!page) {
      return;
    }
    trackPosterBuilderGA4Events(GA4EventName.LAYERS_SELECTED);
    if (memberToGroupId.has(uid)) {
      replaceCurrentSelection(page, [uid], new MouseEvent(DRILL_IN_EVENT_TYPE));
      return;
    }
    toggleSelection(page, [uid]);
  };

  const handleGroupClick = (groupId: string): void => {
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!page) {
      return;
    }
    trackPosterBuilderGA4Events(GA4EventName.LAYERS_SELECTED);
    toggleSelection(page, page.items.groups.getItemsByGroupId(groupId).map((m) => m.uid));
  };

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [lockedListHeight, setLockedListHeight] = useState<number | null>(null);
  const dragStartRectRef = useRef<ClientRect | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const mouseSensor = useSensor(MouseSensor, {activationConstraint: {distance: DRAG_DISTANCE_PX}});
  const touchSensor = useSensor(TouchSensor, {activationConstraint: {delay: TOUCH_LONG_PRESS_DELAY_MS, tolerance: TOUCH_LONG_PRESS_TOLERANCE_PX}});
  const sensors = useSensors(mouseSensor, touchSensor);

  useEffect((): (() => void) => {
    // scroll the selected row into view when the panel is mounted
    const frameId = requestAnimationFrame((): void => {
      listRef.current?.querySelector<HTMLElement>(`.${layerRowStyles.selected}`)?.scrollIntoView({block: 'center'});
    });
    return (): void => {
      cancelAnimationFrame(frameId);
    };
  }, []);

  // always return the rect captured at drag start: collapsing groups can shift the dragged row, and a re-measure would yank the overlay away from the pointer
  const measureDraggable = useCallback((node: HTMLElement): ClientRect => {
    if (dragStartRectRef.current) {
      return dragStartRectRef.current;
    }
    const rect = node.getBoundingClientRect();
    dragStartRectRef.current = rect;
    return rect;
  }, []);

  const measuring = useMemo(() => ({draggable: {measure: measureDraggable}}), [measureDraggable]);

  const collisionDetection: CollisionDetection = useCallback((args) => {
    const collisions = rectIntersection(args);
    if (collisions.length > 0) {
      return collisions;
    }
    const listRect = listRef.current?.getBoundingClientRect();
    const pointer = args.pointerCoordinates;
    if (!listRect || !pointer) {
      return collisions;
    }
    // pointer is over the empty space the collapse left in the height-locked list: snap to the closest row so the drop lands at the end
    const isPointerInsideList = pointer.x >= listRect.left && pointer.x <= listRect.right && pointer.y >= listRect.top && pointer.y <= listRect.bottom;
    return isPointerInsideList ? closestCenter(args) : collisions;
  }, []);

  const onDragStart = useCallback((event: DragStartEvent): void => {
    // lock the list height in the same render as the group collapse, so the popup never resizes (and repositions) mid-drag
    setLockedListHeight(listRef.current?.getBoundingClientRect().height ?? null);
    setActiveDragId(String(event.active.id));
  }, []);

  const resetDragState = useCallback((): void => {
    setActiveDragId(null);
    setLockedListHeight(null);
    dragStartRectRef.current = null;
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent): void => {
      resetDragState();
      handleDragEnd(event, entries);
      if (event.over && event.active.id !== event.over.id) {
        trackPosterBuilderGA4Events(GA4EventName.LAYER_ARRANGED);
      }
    },
    [entries, resetDragState]
  );

  const draggedLayer = useMemo(() => (activeDragId ? getDraggedLayer(activeDragId, entries) : null), [activeDragId, entries]);

  const isGroupExpanded = (entry: LayerGroupEntry): boolean => {
    // while dragging, only the group containing the dragged member stays expanded
    if (activeDragId !== null) {
      return memberToGroupId.get(activeDragId) === entry.groupId;
    }
    return entry.memberUids.some((uid) => activeIdsSet.has(uid));
  };

  const isGroupFullySelected = (entry: LayerGroupEntry): boolean => {
    return entry.memberUids.length > 0 && entry.memberUids.every((uid) => activeIdsSet.has(uid));
  };

  const renderGroupMembers = (entry: LayerGroupEntry, isExpanded: boolean): ReactElement | null => {
    if (!isExpanded) {
      return null;
    }
    return (
      <SortableContext items={entry.memberUids} strategy={verticalListSortingStrategy}>
        <div className={`${styles.groupMembers} flex-v-row gap-2`}>
          {entry.memberUids.map((uid) => (
            <LayerRow key={uid} uid={uid} isSelected={activeIdsSet.has(uid)} isInsideGroup onClick={handleItemClick} />
          ))}
        </div>
      </SortableContext>
    );
  };

  const renderEntry = (entry: LayerEntry): ReactElement => {
    if (entry.layerType === 'item') {
      return <LayerRow key={entry.uid} uid={entry.uid} isSelected={activeIdsSet.has(entry.uid)} disableDrag={isSingleEntry} onClick={handleItemClick} />;
    }
    const isGroupSelected = isGroupFullySelected(entry);
    const isExpanded = isGroupExpanded(entry);
    return (
      <React.Fragment key={entry.groupId}>
        <LayerGroupRow groupId={entry.groupId} isExpanded={isExpanded} isSelected={isGroupSelected} disableDrag={isSingleEntry} onClick={handleGroupClick} />
        {renderGroupMembers(entry, isExpanded)}
      </React.Fragment>
    );
  };

  const renderOverlay = (): ReactElement | null => {
    if (!draggedLayer) {
      return null;
    }
    const row =
      draggedLayer.layerType === 'group' ? (
        <LayerGroupRow groupId={draggedLayer.entry.groupId} isExpanded={false} isSelected={isGroupFullySelected(draggedLayer.entry)} isOverlay />
      ) : (
        <LayerRow uid={draggedLayer.uid} isSelected={activeIdsSet.has(draggedLayer.uid)} isOverlay />
      );
    return <div className={`${styles.overlayCard} flex-v-row gap-2 _full-width`}>{row}</div>;
  };

  const panelClasses = `${styles.panel} flex-v-row _full-width ${hideHeader ? styles.inModal : ''}`;

  const renderHeader = (): ReactElement | null => {
    if (hideHeader) {
      return null;
    }
    return (
      <div className={`${styles.header} flexbox flex-items-center flex-justify-between flex-shrink-0 spacing-m-b-2`}>
        <Text val={window.i18next.t('pmwjs_select_layers')} size={TextSize.MD} bold />
        <ClickableDiv onClick={onClose} className={`${styles.closeButton} flex-center`} aria-label={window.i18next.t('pmwjs_close')}>
          <Icon icon="icon-close" size={IconSize.SIZE_ICON_16} />
        </ClickableDiv>
      </div>
    );
  };

  const renderEmptyState = (): ReactElement => {
    return (
      <div className={`${styles.emptyState} flex-v-row flex-align-items-center gap-4`}>
        <div className={`${styles.emptyStateIcon} flex-center`}>
          <Icon icon="icon-ban" size={IconSize.SIZE_ICON_24} />
        </div>
        <Text size={TextSize.SM} val={window.i18next.t('pmwjs_layers_panel_empty')} />
      </div>
    );
  };

  const renderBody = (): ReactElement => {
    if (entries.length === 0) {
      return renderEmptyState();
    }
    return (
      <DndContext sensors={sensors} modifiers={DRAG_MODIFIERS} collisionDetection={collisionDetection} measuring={measuring} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={resetDragState}>
        <SortableContext items={outerIds} strategy={verticalListSortingStrategy}>
          <div ref={listRef} className="flex-v-row gap-2" style={lockedListHeight ? {minHeight: lockedListHeight} : undefined}>
            {entries.map(renderEntry)}
          </div>
        </SortableContext>
        {createPortal(
          <DragOverlay dropAnimation={null} zIndex={DRAG_OVERLAY_Z_INDEX_ABOVE_POPUPS}>
            {renderOverlay()}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    );
  };

  return (
    <div className={panelClasses}>
      {renderHeader()}
      <div ref={bodyRef} className={`${styles.body} flex-v-row flex-1`}>{renderBody()}</div>
    </div>
  );
}

